import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword, resendResetCode } from '../api'; // Добавляем функцию resendResetCode
import styles from './HomePage.module.css';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number>(0); // Счетчик для повторной отправки
  const email = (location.state as { email?: string })?.email || '';

  const navigateToSection = (sectionId: string): void => {
    navigate(`/#${sectionId}`);
  };

  const startNewChat = (): void => {
    navigate('/chat');
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!email) {
      setError('Email не указан. Вернитесь к сбросу пароля.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    try {
      const response = await resetPassword(email, code, password);
      if (response.success) {
        setMessage('Пароль успешно изменён. Перенаправляем на страницу входа...');
        setError(null);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(response.message || 'Неверный код или ошибка');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при сбросе пароля');
    }
  };

  const handleResendCode = async (): Promise<void> => {
    try {
      const response = await resendResetCode(email);
      if (response.success) {
        setMessage('Новый код отправлен на ваш email.');
        setResendCooldown(60); // Устанавливаем таймер на 60 секунд
        setError(null);
      } else {
        setError(response.message || 'Ошибка при повторной отправке кода');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при повторной отправке кода');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!email) {
      setError('Email не указан. Вернитесь к сбросу пароля.');
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  return (
    <div className={styles.homeContainer}>
      <header className={styles.homeHeader}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <div className={styles.homeLogo}>
              <h1>
                GPT4Chats<span>.</span>
              </h1>
            </div>
            <nav className={styles.homeNav}>
              <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                Главная
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                Особенности
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                Вопросы
              </button>
              <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                Начать
              </button>
            </nav>
          </div>
        </div>
      </header>

      <section className={styles.authSection}>
        <div className={styles.authContainer}>
          <h1>Сброс пароля</h1>
          <p className={styles.authIntro}>
            Введите код из email и новый пароль для {email}.
          </p>
          <form onSubmit={handleResetPassword} className={styles.authForm}>
            <div>
              <label htmlFor="code">Код сброса</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                required
                className={styles.authInput}
                placeholder="Введите 6-значный код"
              />
            </div>
            <div>
              <label htmlFor="password">Новый пароль</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
                className={styles.authInput}
                placeholder="Введите новый пароль"
              />
            </div>
            <div>
              <label htmlFor="confirmPassword">Подтвердите пароль</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmPassword(e.target.value)
                }
                required
                className={styles.authInput}
                placeholder="Подтвердите новый пароль"
              />
            </div>
            {error && <p className={styles.authError}>{error}</p>}
            {message && <p className={styles.authIntro}>{message}</p>}
            <button type="submit" className={styles.authButton}>
              Сбросить пароль
            </button>
          </form>
          <div className={styles.policyFooter}>
            <button className={styles.authLink} onClick={() => navigate('/login')}>
              Вернуться к входу
            </button>
            <span className={styles.authSep}>•</span>
            <button className={styles.authLink} onClick={() => navigate('/register')}>
              Регистрация
            </button>
            <span className={styles.authSep}>•</span>
            <button
              className={`${styles.authLink} ${resendCooldown > 0 ? styles.disabled : ''}`}
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
            >
              Отправить код повторно {resendCooldown > 0 ? `(${resendCooldown} сек)` : ''}
            </button>
          </div>
        </div>
      </section>

      <footer className={styles.homeFooter}>
        <div className={styles.container}>
          <div className={styles.footerContent}>
            <div className={styles.footerSection}>
              <h4>
                GPT4Chats<span>.</span>
              </h4>
              <p>Ваш ИИ-помощник для работы и творчества.</p>
              <p>GPT4Chats.ru</p>
            </div>
            <div className={styles.footerSection}>
              <h4>Навигация</h4>
              <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                Главная
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                Особенности
              </button>
              <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                Вопросы
              </button>
            </div>
            <div className={styles.footerSection}>
              <h4>Документы</h4>
              <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                Политика конфиденциальности
              </button>
              <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                Условия использования
              </button>
            </div>
            <div className={styles.footerSection}>
              <h4>Контакты</h4>
              <p>Email: support@gpt4chats.ru</p>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResetPasswordPage;